// garuda - Markdown Renderer with KaTeX Support

import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import rehypeSlug from "rehype-slug";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import "katex/dist/katex.min.css";
import "./MarkdownRenderer.css";

// Custom Code Block Component with Copy Button
const CodeBlock = ({ language, children }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="code-block-wrapper">
      <div className="code-block-header">
        <span className="code-block-language">{language}</span>
        <button
          className="code-block-copy-btn"
          onClick={handleCopy}
          title="Copy code"
        >
          {copied ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          )}
        </button>
      </div>
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={language}
        PreTag="div"
        showLineNumbers={false}
        lineProps={() => ({
          style: {
            display: "block",
            border: "none",
            background: "none",
            padding: "0",
            margin: "0",
            outline: "none",
            boxShadow: "none",
          },
        })}
        customStyle={{
          margin: 0,
          borderRadius: "0 0 4px 4px",
          background: "var(--color-code-bg)",
        }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
};

const MarkdownRenderer = ({ content, className = "" }) => {
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkMath, remarkGfm]}
        rehypePlugins={[rehypeKatex, rehypeSlug]}
        components={{
          // Custom code block rendering with syntax highlighting
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            const language = match ? match[1] : "text";

            return !inline && match ? (
              <CodeBlock language={language}>
                {String(children).replace(/\n$/, "")}
              </CodeBlock>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          // Custom blockquote rendering
          blockquote({ node, children, ...props }) {
            return (
              <blockquote className="markdown-blockquote" {...props}>
                {children}
              </blockquote>
            );
          },
          // Custom heading rendering
          h1({ node, children, ...props }) {
            return (
              <h1 className="markdown-h1" {...props}>
                {children}
              </h1>
            );
          },
          h2({ node, children, ...props }) {
            return (
              <h2 className="markdown-h2" {...props}>
                {children}
              </h2>
            );
          },
          h3({ node, children, ...props }) {
            return (
              <h3 className="markdown-h3" {...props}>
                {children}
              </h3>
            );
          },
          // Custom link rendering
          a({ node, children, href, ...props }) {
            // Check if it's an anchor link (starts with #)
            const isAnchor = href && href.startsWith("#");

            return (
              <a
                href={href}
                target={isAnchor ? undefined : "_blank"}
                rel={isAnchor ? undefined : "noopener noreferrer"}
                className="markdown-link"
                {...props}
              >
                {children}
              </a>
            );
          },
          // Custom image rendering with width support
          img({ node, src, alt, ...props }) {
            // Parse width from alt text (e.g., "My Image|50%" or "Image|200px")
            let width = "100%";
            let cleanAlt = alt || "";

            if (alt && alt.includes("|")) {
              const parts = alt.split("|");
              cleanAlt = parts[0].trim();
              width = parts[1].trim();
            }

            // Handle relative paths from public folder
            let imageSrc = src;
            const isRelative =
              src && !src.startsWith("http") && !src.startsWith("/");

            if (isRelative) {
              // Convert relative path to absolute from public folder
              // e.g., "MTM Notes/image.png" -> "/content/notes/Semester6/MTM/image.png"
              imageSrc = src;

              // Try to construct proper path - this is a fallback
              if (!src.startsWith("content/")) {
                // Show placeholder with instructions
                return (
                  <div className="markdown-image-placeholder">
                    <span className="image-placeholder-text">
                      Image: {cleanAlt || "Untitled"}
                    </span>
                    <span className="image-placeholder-path">{src}</span>
                    <span className="image-placeholder-hint">
                      Update path to absolute from /public (e.g.,
                      /content/notes/...)
                    </span>
                  </div>
                );
              }
              imageSrc = `/${src}`;
            }

            return (
              <img
                src={imageSrc}
                alt={cleanAlt}
                loading="lazy"
                style={{ width, maxWidth: "100%", height: "auto" }}
                onError={(e) => {
                  e.target.style.display = "none";
                  const placeholder = document.createElement("div");
                  placeholder.className = "markdown-image-placeholder";
                  placeholder.innerHTML = `
                    <span class="image-placeholder-text">Failed to load: ${cleanAlt || "Image"}</span>
                    <span class="image-placeholder-path">${src}</span>
                  `;
                  e.target.parentNode.replaceChild(placeholder, e.target);
                }}
                {...props}
              />
            );
          },
          // Custom list rendering
          ul({ node, children, ...props }) {
            return (
              <ul className="markdown-ul" {...props}>
                {children}
              </ul>
            );
          },
          ol({ node, children, ...props }) {
            return (
              <ol className="markdown-ol" {...props}>
                {children}
              </ol>
            );
          },
          // Custom checkbox rendering
          input({ node, type, checked, ...props }) {
            if (type === "checkbox") {
              return (
                <input
                  type="checkbox"
                  checked={checked}
                  disabled
                  className="markdown-checkbox"
                  {...props}
                />
              );
            }
            return <input type={type} {...props} />;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
